import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { filter, Subscription } from 'rxjs';

import { MENU_ITEMS } from '../core/config/menu.config';
import { MenuItem } from '../core/models/menu.model';
import { AuthService } from '../core/services/auth.service';

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    ButtonModule,
    AvatarModule,
    BadgeModule,
    TooltipModule,
  ],
  styles: [
    ':host { display: block; }',
    '.menu-link-active { background-color: rgba(255, 255, 255, 0.12); color: #fff; }',
    ':host ::ng-deep .menu-tooltip { border: 1px solid #123264; box-shadow: 0 8px 20px rgba(0, 0, 0, 0.35); }',
    ':host ::ng-deep .menu-tooltip .p-tooltip-text { background-color: #0b2246; color: #fff; opacity: 1; padding: 10px 12px; border-radius: 10px; font-weight: 600; letter-spacing: 0.01em; }',
    ':host ::ng-deep .menu-tooltip .p-tooltip-arrow { display: none; }',
  ],
  template: `
    <div class="flex min-h-screen bg-[var(--bg-soft)] text-slate-800 relative">
      <!-- Mobile overlay -->
      <div
        class="fixed inset-0 bg-black/30 backdrop-blur-sm z-30 lg:hidden"
        *ngIf="!isDesktop && mobileOpen"
        (click)="toggleSidebar()"
        aria-hidden="true"
      ></div>

      <!-- Sidebar -->
      <aside
        class="bg-brand-900 text-white flex flex-col fixed inset-y-0 left-0 z-40 transform transition-transform duration-200"
        [class.-translate-x-full]="!isDesktop && !mobileOpen"
        [style.width.px]="asideWidth"
      >
        <!-- Brand header -->
        <div class="flex items-center px-5 py-4 gap-3 border-b border-white/10">
          <span class="pi pi-shield text-xl text-sky-100"></span>
          <div class="leading-tight" *ngIf="!collapseCompact">
            <p class="text-xs uppercase text-white/60 tracking-wide">Administración</p>
            <p class="font-semibold">JM Biomédica</p>
          </div>
        </div>

        <!-- Navigation -->
        <nav class="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <a
            *ngFor="let item of menu; trackBy: trackByLabel"
            [routerLink]="item.path"
            routerLinkActive="menu-link-active"
            class="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition hover:bg-white/10 hover:text-white"
            (click)="handleNavClick()"
            pTooltip="{{ item.label }}"
            tooltipStyleClass="menu-tooltip"
            tooltipPosition="right"
            [tooltipDisabled]="!collapseCompact"
          >
            <i [class]="item.icon"></i>
            <span class="flex-1" *ngIf="!collapseCompact">{{ item.label }}</span>
            <span *ngIf="item.badge" class="text-xs bg-white/20 px-2 py-0.5 rounded-full">{{
              item.badge
            }}</span>
          </a>
        </nav>

        <!-- User footer -->
        <div
          class="px-4 py-4 border-t border-white/10"
          *ngIf="!collapseCompact; else compactUser"
        >
          <div class="flex items-center gap-3 mb-3">
            <p-avatar [label]="userInitials" shape="circle" styleClass="bg-white/10"></p-avatar>
            <div class="text-sm leading-tight min-w-0 flex-1">
              <p class="font-semibold truncate">{{ userName }}</p>
              <p class="text-white/70 truncate text-xs">{{ userRole }}</p>
            </div>
          </div>
          <button
            pButton
            type="button"
            label="Cerrar sesión"
            icon="pi pi-sign-out"
            class="p-button-text p-button-sm w-full text-white/80 hover:text-white"
            (click)="logout()"
          ></button>
        </div>

        <ng-template #compactUser>
          <div class="px-4 py-4 border-t border-white/10 flex flex-col items-center gap-2">
            <p-avatar [label]="userInitials" shape="circle" styleClass="bg-white/10"></p-avatar>
            <button
              pButton
              type="button"
              icon="pi pi-sign-out"
              class="p-button-text p-button-rounded p-button-sm text-white/80"
              pTooltip="Cerrar sesión"
              tooltipPosition="right"
              (click)="logout()"
            ></button>
          </div>
        </ng-template>
      </aside>

      <!-- Main content -->
      <div class="flex-1 flex flex-col" [style.marginLeft.px]="contentMargin">
        <!-- Top header bar -->
        <header
          class="h-14 sm:h-16 bg-white border-b border-[var(--border)] px-2 sm:px-5 lg:px-6 flex items-center justify-between gap-2 sm:gap-3 shadow-sm"
        >
          <div
            class="flex items-center gap-1 sm:gap-3 text-xs sm:text-sm text-slate-600 min-w-0 flex-1"
          >
            <button
              pButton
              type="button"
              class="p-button-text p-button-rounded p-button-sm"
              icon="pi pi-bars"
              (click)="toggleSidebar()"
              [attr.aria-expanded]="toggleExpanded"
              aria-label="Alternar menú"
            ></button>
            <span class="text-brand-700 font-semibold truncate">JM Biomédica</span>
            <span class="text-slate-400 hidden sm:inline">/</span>
            <span class="hidden sm:inline truncate">{{ currentPageLabel }}</span>
          </div>
          <div class="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <button
              pButton
              icon="pi pi-bell"
              class="p-button-text p-button-rounded p-button-sm lg:p-button"
              pBadge
              value="0"
            ></button>
            <p-avatar [label]="userInitials" shape="circle" styleClass="shadow-sm"></p-avatar>
          </div>
        </header>

        <!-- Page content -->
        <main class="p-3 sm:p-4 md:p-6">
          <router-outlet></router-outlet>
        </main>
      </div>
    </div>
  `,
})
export class ShellComponent implements OnInit, OnDestroy {
  readonly menu = MENU_ITEMS;

  isDesktop =
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true;
  sidebarCollapsed = false;
  mobileOpen = false;
  currentPageLabel = 'Panel';

  private mediaQuery: MediaQueryList | null =
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)') : null;
  private mediaListener?: (event: MediaQueryListEvent) => void;
  private routerSub?: Subscription;

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
  ) {}

  trackByLabel(_index: number, item: MenuItem) {
    return item.label;
  }

  /** User display helpers — derived from auth signal */
  get userName(): string {
    return this.authService.currentUser()?.fullName ?? 'Usuario';
  }

  get userRole(): string {
    const roles = this.authService.currentUser()?.roles ?? [];
    if (roles.length === 0) return '';
    // Strip ROLE_ prefix for display
    return roles.map((r) => r.replace(/^ROLE_/i, '')).join(', ');
  }

  get userInitials(): string {
    const name = this.userName;
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  }

  toggleSidebar() {
    if (this.isDesktop) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
    } else {
      this.mobileOpen = !this.mobileOpen;
    }
  }

  handleNavClick() {
    if (!this.isDesktop) {
      this.mobileOpen = false;
    }
  }

  logout() {
    this.authService.logout();
  }

  ngOnInit() {
    if (this.mediaQuery) {
      this.mediaListener = (event: MediaQueryListEvent) => {
        this.isDesktop = event.matches;
        if (event.matches) {
          this.mobileOpen = false;
        } else {
          this.sidebarCollapsed = false;
        }
      };
      this.mediaQuery.addEventListener('change', this.mediaListener);
    }

    this.updateBreadcrumbFromUrl(this.router.url);
    this.routerSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.updateBreadcrumbFromUrl(event.urlAfterRedirects));
  }

  ngOnDestroy() {
    if (this.mediaQuery && this.mediaListener) {
      this.mediaQuery.removeEventListener('change', this.mediaListener);
    }
    this.routerSub?.unsubscribe();
  }

  private updateBreadcrumbFromUrl(url: string) {
    const segment = url.split('/').filter(Boolean)[0] ?? 'dashboard';
    const match = this.menu.find((item) => (item.path ?? '').replace('/', '') === segment);
    this.currentPageLabel = match?.label ?? 'Panel';
  }

  get collapseCompact() {
    return this.isDesktop && this.sidebarCollapsed;
  }
  get asideWidth() {
    return this.isDesktop ? (this.sidebarCollapsed ? 72 : 256) : 256;
  }
  get contentMargin() {
    return this.isDesktop ? (this.sidebarCollapsed ? 72 : 256) : 0;
  }
  get toggleExpanded() {
    return this.isDesktop ? !this.sidebarCollapsed : this.mobileOpen;
  }
}
